<template>
  <v-card elevation="0" outlined :class="['breakdown-card', { 'flat-breakdown': flat }]">
    <!-- Empty state -->
    <div v-if="!breakdown || breakdown.length === 0" class="py-4 px-6 grey--text caption">
      <v-icon small color="grey" class="mr-1">mdi-information-outline</v-icon>
      {{ $t('billing.breakdown.empty') }}
    </div>

    <!-- Breakdown table -->
    <v-simple-table v-else class="breakdown-bg" dense>
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left breakdown-header">{{ $t('billing.breakdown.colDate') }}</th>
            <th class="text-left breakdown-header">{{ $t('billing.breakdown.colLabel') }}</th>
            <th class="text-left breakdown-header">{{ $t('billing.breakdown.colClient') }}</th>
            <th class="text-center breakdown-header">{{ $t('billing.breakdown.bankName') }}</th>
            <th class="text-right breakdown-header">{{ $t('billing.breakdown.colAmount') }}</th>
            <th class="text-center breakdown-header">{{ $t('billing.breakdown.colCcy') }}</th>
            <th class="text-right breakdown-header">{{ $t('billing.breakdown.colVolumeBrl') }}</th>
            <!-- Role-aware revenue headers -->
            <template v-if="brokerLayout">
              <th class="text-right breakdown-header">{{ $t('billing.breakdown.colRevenuePartner') }}</th>
              <th class="text-right breakdown-header">{{ $t('billing.breakdown.colRevenueBroker') }}</th>
              <th class="text-right breakdown-header">{{ $t('billing.breakdown.colRevenueTotal') }}</th>
            </template>
            <template v-else>
              <th class="text-right breakdown-header">{{ $t('billing.breakdown.colRevenue') }}</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in breakdown"
            :key="row.rfq_id"
            class="breakdown-row"
          >
            <td class="breakdown-cell">
              <span :title="row.timestamp ? formatFullTimestamp(row.timestamp) : ''">
                {{ formatTimestamp(row.timestamp) }}
              </span>
            </td>
            <td class="breakdown-cell">{{ row.label }}</td>
            <td class="breakdown-cell">
              <span :title="row.client_name">
                {{ clampText(row.client_name, 25) }}
              </span>
            </td>
            <td class="breakdown-cell text-center">{{ row.bank_name }}</td>
            <td class="breakdown-cell text-right font-family-monospace">{{ formatVolume(row.volume) }}</td>
            <td class="breakdown-cell text-center">{{ row.ccy }}</td>
            <td class="breakdown-cell text-right font-family-monospace">{{ formatRevenue(row.volume_brl) }}</td>
            <!-- Role-aware revenue cells -->
            <template v-if="brokerLayout">
              <td class="breakdown-cell text-right font-family-monospace">
                {{ formatRevenue(getPartnerRevenue(row)) }}
              </td>
              <td class="breakdown-cell text-right font-family-monospace font-weight-bold"
                :class="getBrokerRevenue(row) < 0 ? 'error--text' : 'success--text'">
                {{ formatRevenue(getBrokerRevenue(row)) }}
              </td>
              <td class="breakdown-cell text-right font-family-monospace font-weight-bold">
                {{ formatRevenue(getTotalRevenue(row)) }}
              </td>
            </template>
            <template v-else>
              <td class="breakdown-cell text-right font-family-monospace font-weight-bold"
                :class="getPartnerRevenueValue(row) < 0 ? 'error--text' : 'success--text'">
                {{ formatRevenue(getPartnerRevenueValue(row)) }}
              </td>
            </template>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </v-card>
</template>

<script>
import { formatDate, formatBRLPretty, formatTimeAndDate, formatPrice } from "@/utils";

export default {
  name: "InvoiceBreakdownTable",
  props: {
    breakdown: {
      type: Array,
      default: () => [],
    },
    flat: {
      type: Boolean,
      default: false,
    },
    brokerLayout: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    formatTimestamp(ts) {
      return formatDate(ts);
    },
    formatFullTimestamp(ts) {
      if (!ts) return "";
      return formatTimeAndDate(ts);
    },
    formatRevenue(revenue) {
      const n = Number(revenue);
      if (!Number.isFinite(n)) return "-";
      // need the negative sign before R$
      return n >= 0 ? `R$ ${formatBRLPretty(n)}` : `-R$ ${formatBRLPretty(-n)}`;
    },
    formatVolume(volume) {
      const n = Number(volume);
      if (!Number.isFinite(n)) return "-";
      return formatPrice(n);
    },
    getPartnerRevenueValue(row) {
      if (row.revenue != null) return row.revenue;
      return row.revenue_partner;
    },
    clampText(text, limit) {
      if (!text) return "-";
      if (text.length > limit) {
        return text.substring(0, limit - 3) + "...";
      }
      return text;
    },
    getPartnerRevenue(row) {
      if (row.revenue_partner == null) return undefined;
      return row.revenue_partner;
    },
    getBrokerRevenue(row) {
      if (row.revenue_broker != null) return row.revenue_broker;
      return row.revenue;
    },
    getTotalRevenue(row) {
      if (row.revenue_total != null) return row.revenue_total;
      return row.revenue;
    },
  },
};
</script>

<style scoped>
.breakdown-bg {
  background: #111111 !important;
}

.breakdown-card {
  border-radius: 8px !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
  overflow: hidden;
}

.breakdown-card.flat-breakdown {
  border-top-right-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
  border-right: none !important;
  background: transparent !important;
}

.breakdown-card.flat-breakdown .breakdown-row:last-child .breakdown-cell {
  border-bottom: none !important;
}

.breakdown-header {
  color: #ffffff !important;
  font-size: 0.72rem !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px;
  padding: 10px 12px !important;
  white-space: nowrap;
}

.breakdown-cell {
  font-size: 0.82rem !important;
  color: rgba(255, 255, 255, 0.75) !important;
  padding: 8px 12px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04) !important;
}

.breakdown-row:last-child .breakdown-cell {
  border-bottom: none !important;
}
</style>
